import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Barbell, Lightning, ChartLineUp, Ruler, User, X, ChatCircle } from "@phosphor-icons/react";

import { API } from "../components/shared/constants";
import { ClientProgressDashboard } from "../components/shared/ClientProgressDashboard";
import { WorkoutsView } from "../components/shared/WorkoutsView";
import { MeasurementsView } from "../components/shared/MeasurementsView";
import { ExercisesView } from "../components/shared/ExercisesView";
import { PhasesView } from "../components/shared/PhasesView";
import { RestTimer } from "../components/shared/RestTimer";
import { TodoTodaySection } from "../components/shared/TodoTodaySection";
import BadgesView from "../components/shared/BadgesView";
import { BadgeCelebration } from "../components/shared/BadgeCelebration";
import CheckinForm from "../components/shared/CheckinForm";

export default function SharedClientPage() {
  const { token } = useParams();
  const [client, setClient] = useState(null);
  const [phases, setPhases] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [workouts, setWorkouts] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("schede");
  const [badgesOpen, setBadgesOpen] = useState(false);
  const [badges, setBadges] = useState(null);
  const [badgesLoading, setBadgesLoading] = useState(false);
  const [celebrationQueue, setCelebrationQueue] = useState([]);
  const [currentCelebration, setCurrentCelebration] = useState(null);
  const [seenBadgeCodes, setSeenBadgeCodes] = useState(() => new Set());

  const seenKey = client?.id ? `fitpro-seen-badges-${client.id}` : null;

  // Load seen set from localStorage when client.id becomes available
  useEffect(() => {
    if (!seenKey) return;
    try {
      const raw = window.localStorage.getItem(seenKey);
      if (raw) setSeenBadgeCodes(new Set(JSON.parse(raw)));
    } catch (err) { console.debug("[SharedClientPage]", err); }
  }, [seenKey]);

  const persistSeen = useCallback((set) => {
    if (!seenKey) return;
    try {
      window.localStorage.setItem(seenKey, JSON.stringify(Array.from(set)));
    } catch (err) { console.debug("[SharedClientPage]", err); }
  }, [seenKey]);

  const fetchBadges = useCallback(async () => {
    try {
      const res = await fetch(`${API}/shared/${token}/badges`);
      if (res.ok) setBadges(await res.json());
    } catch (err) { console.debug("[SharedClientPage]", err); }
  }, [token]);

  const fetchAll = useCallback(async () => {
    try {
      const [clientRes, phasesRes, programsRes, workoutsRes, measRes, exRes, logsRes] = await Promise.all([
        fetch(`${API}/shared/${token}`),
        fetch(`${API}/shared/${token}/phases`),
        fetch(`${API}/shared/${token}/programs`),
        fetch(`${API}/shared/${token}/workouts`),
        fetch(`${API}/shared/${token}/measurements`),
        fetch(`${API}/shared/${token}/exercises`),
        fetch(`${API}/shared/${token}/logs`),
      ]);
      if (!clientRes.ok) throw new Error("Link non valido");
      setClient(await clientRes.json());
      setPhases(await phasesRes.json());
      setPrograms(await programsRes.json());
      setWorkouts(await workoutsRes.json());
      setMeasurements(await measRes.json());
      setExercises(await exRes.json());
      setLogs(await logsRes.json());
    } catch (err) {
      setError(err.message || "Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  }, [token]);

  const refreshLogs = useCallback(async (newlyBadges) => {
    try {
      const res = await fetch(`${API}/shared/${token}/logs`);
      if (res.ok) setLogs(await res.json());
    } catch (err) { console.debug("[SharedClientPage]", err); }
    if (Array.isArray(newlyBadges) && newlyBadges.length) {
      setCelebrationQueue((q) => [...q, ...newlyBadges]);
      // Refetch badges so the indicator updates even before the user opens the modal
      fetchBadges();
    }
  }, [token, fetchBadges]);

  // Dequeue celebrations one at a time
  useEffect(() => {
    if (!currentCelebration && celebrationQueue.length > 0) {
      setCurrentCelebration(celebrationQueue[0]);
      setCelebrationQueue((q) => q.slice(1));
    }
  }, [celebrationQueue, currentCelebration]);

  const dismissCelebration = useCallback(() => {
    setCurrentCelebration(null);
  }, []);

  const openBadges = useCallback(async () => {
    setBadgesOpen(true);
    setBadgesLoading(true);
    try {
      const res = await fetch(`${API}/shared/${token}/badges`);
      if (res.ok) {
        const data = await res.json();
        setBadges(data);
        // Mark all currently unlocked badges as seen
        const unlockedCodes = (data?.definitions || []).filter((b) => b.unlocked).map((b) => b.code);
        if (unlockedCodes.length) {
          setSeenBadgeCodes((prev) => {
            const next = new Set(prev);
            unlockedCodes.forEach((c) => next.add(c));
            persistSeen(next);
            return next;
          });
        }
      }
    } catch (err) { console.debug("[SharedClientPage]", err); } finally {
      setBadgesLoading(false);
    }
  }, [token, persistSeen]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Fetch badges once the client loads (to show the counter badge)
  useEffect(() => {
    if (client?.id) fetchBadges();
  }, [client?.id, fetchBadges]);

  // Lock background scroll + close on Escape while the badges modal is open
  // so the page underneath is fully inert.
  useEffect(() => {
    if (!badgesOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleKey = (e) => {
      if (e.key === "Escape") setBadgesOpen(false);
    };
    window.addEventListener("keydown", handleKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", handleKey);
    };
  }, [badgesOpen]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#007AFF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !client) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6">
        <div className="text-center">
          <div className="w-14 h-14 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
            <User size={28} className="text-red-400" />
          </div>
          <h1 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            LINK NON VALIDO
          </h1>
          <p className="text-zinc-500 text-sm">Questo link non è valido o è stato revocato dal trainer.</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: "schede", label: "Schede", icon: Barbell },
    { id: "misure", label: "Misure", icon: Ruler },
    { id: "fasi", label: "Fasi", icon: Lightning },
    { id: "esercizi", label: "Esercizi", icon: ChartLineUp },
    { id: "checkin", label: "Check-in", icon: ChatCircle },
  ];

  const unlockedCodes = (badges?.definitions || []).filter((b) => b.unlocked).map((b) => b.code);
  const newBadgesCount = unlockedCodes.filter((c) => !seenBadgeCodes.has(c)).length;

  return (
    <div className="min-h-screen bg-[#0A0A0A] pb-20" data-testid="shared-client-page">
      <div className="sticky top-0 z-10 bg-[#0A0A0A]/95 backdrop-blur-md border-b border-[#1A1A1A] px-4 py-4">
        <div className="flex items-center gap-2 sm:gap-3 max-w-lg mx-auto">
          <button
            type="button"
            onClick={openBadges}
            className="relative w-10 h-10 rounded-full bg-[#007AFF]/20 hover:bg-[#007AFF]/30 flex items-center justify-center text-[#007AFF] font-bold text-sm flex-shrink-0 transition-colors active:scale-95 outline-none"
            aria-label={newBadgesCount > 0 ? `Vedi i tuoi badge (${newBadgesCount} nuovi)` : "Vedi i tuoi badge"}
            data-testid="shared-open-badges"
          >
            {client.first_name?.charAt(0)?.toUpperCase()}{client.last_name?.charAt(0)?.toUpperCase()}
            {newBadgesCount > 0 && (
              <span
                className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF3B30] border-2 border-[#0A0A0A] text-white text-[10px] font-bold flex items-center justify-center leading-none animate-pulse"
                data-testid="new-badges-count"
              >
                {newBadgesCount > 9 ? "9+" : newBadgesCount}
              </span>
            )}
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-bold tracking-tight text-white truncate" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {client.first_name} {client.last_name}
            </h1>
            <p className="text-[11px] text-zinc-500 tracking-wide truncate">Il tuo percorso di allenamento</p>
          </div>
          <RestTimer shareToken={token} />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 pt-4">
        {activeTab === "schede" && (
          <>
            <ClientProgressDashboard phases={phases} workouts={workouts} logs={logs} programs={programs} />
            <TodoTodaySection
              programs={programs}
              workouts={workouts}
              phases={phases}
              logs={logs}
              token={token}
              exerciseLibrary={exercises}
              onLogSaved={refreshLogs}
              clientName={client?.first_name}
            />
            <WorkoutsView programs={programs} workouts={workouts} phases={phases} token={token} logs={logs} onLogSaved={refreshLogs} exerciseLibrary={exercises} clientName={client?.first_name} />
          </>
        )}
        {activeTab === "misure" && <MeasurementsView measurements={measurements} token={token} onAdded={fetchAll} />}
        {activeTab === "fasi" && <PhasesView phases={phases} />}
        {activeTab === "esercizi" && <ExercisesView exercises={exercises} token={token} programs={programs} workouts={workouts} />}
        {activeTab === "checkin" && <CheckinForm token={token} />}
      </div>

      {/* Badge celebration overlay */}
      <BadgeCelebration badge={currentCelebration} onDismiss={dismissCelebration} shareToken={token} />

      {/* Badges modal */}
      {badgesOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="badges-modal-title"
          data-testid="badges-modal"
        >
          <button
            type="button"
            aria-label="Chiudi"
            onClick={() => setBadgesOpen(false)}
            className="absolute inset-0 bg-black/70 backdrop-blur-sm cursor-default"
          />
          <div className="relative w-full max-w-lg max-h-[85vh] bg-[#0A0A0A] border border-[#262626] rounded-2xl flex flex-col overflow-hidden">
            <div className="bg-[#0A0A0A] border-b border-[#1A1A1A] px-4 py-3 flex items-center justify-between">
              <h2
                id="badges-modal-title"
                className="text-base font-bold tracking-tight text-white uppercase"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                I TUOI BADGE
              </h2>
              <button
                type="button"
                onClick={() => setBadgesOpen(false)}
                className="w-8 h-8 rounded-full bg-[#1A1A1A] hover:bg-[#262626] flex items-center justify-center text-zinc-400 hover:text-white"
                aria-label="Chiudi"
                data-testid="badges-modal-close"
              >
                <X size={14} weight="bold" />
              </button>
            </div>
            <div className="overflow-y-auto p-4 pb-6">
              <BadgesView data={badges} loading={badgesLoading} error={null} shareToken={token} />
            </div>
          </div>
        </div>
      )}

      <div className="fixed bottom-0 left-0 right-0 bg-[#0F0F0F]/95 backdrop-blur-md border-t border-[#1A1A1A] z-20">
        <div className="max-w-lg mx-auto flex">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex flex-col items-center gap-1 py-3 transition-colors ${
                  isActive ? "text-[#007AFF]" : "text-zinc-600"
                }`}
                data-testid={`shared-tab-${tab.id}`}
              >
                <Icon size={20} weight={isActive ? "fill" : "regular"} />
                <span className="text-[10px] font-medium">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
